import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CustomerDto, useSearchCustomersQuery } from '@/hooks/customerHooks';
import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Spinner } from '@/components/ui/spinner';
import { createSite } from '../siteApi';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';

interface AddSiteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (params: { customerID: string; siteName: string; siteId: string }) => void;
  customerId: string;
  customerName: string;
  noNavigate?: boolean;
}

export function AddSite({
  open,
  onOpenChange,
  onSubmit,
  customerId,
  customerName,
  noNavigate,
}: AddSiteProps) {
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerDto | null>(null);

  const [siteName, setSiteName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const { data: customerPages } = useSearchCustomersQuery({
    query: searchQuery,
  });
  const customers = customerPages?.pages.flat() ?? [];

  const handleSubmit = async () => {
    if ((selectedCustomer === null && customerId === '') || !siteName) return;

    setIsSubmitting(true);
    try {
      const result = await createSite(selectedCustomer ? selectedCustomer.id : customerId, {
        name: siteName,
      });

      // Navigate to the newly created site
      onSubmit({ customerID: customerId, siteName, siteId: result.project!.id ?? '' });

      if (!noNavigate) {
        navigate(
          `/Customer/${selectedCustomer ? selectedCustomer.id : customerId}/project/${result.project!.id}`
        );
      }
    } catch (error) {
      console.error('Failed to create site:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to create site. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
      setSiteName('');
      setSelectedCustomer(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add Site</DialogTitle>
        </DialogHeader>
        <div className="grid gap-6 px-4 py-6">
          <div className="grid gap-2">
            <Label htmlFor="customer">Customer</Label>
            {customerId !== '' ? (
              <div className="rounded-md border border-input bg-background px-3 py-2 text-sm text-muted-foreground">
                {customerName}
              </div>
            ) : (
              <Popover open={selectorOpen} onOpenChange={setSelectorOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={selectorOpen}
                    className="justify-between"
                  >
                    {selectedCustomer ? selectedCustomer.name : 'Select customer...'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0">
                  <Command>
                    <CommandInput
                      placeholder="Search customers..."
                      value={searchQuery}
                      onValueChange={setSearchQuery}
                    />
                    <CommandList>
                      <CommandEmpty>No customers found.</CommandEmpty>
                      <CommandGroup>
                        {customers.map((customer) => (
                          <CommandItem
                            key={customer.id}
                            onSelect={() => {
                              setSelectedCustomer(customer);
                              setSelectorOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                selectedCustomer?.id === customer.id ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                            {customer.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="name">Site Name</Label>
            <Input
              id="name"
              value={siteName}
              onChange={(e) => setSiteName(e.target.value)}
              className="col-span-3"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>{' '}
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || (!selectedCustomer && customerId === '') || !siteName}
          >
            {isSubmitting ? <Spinner className="mr-2" /> : null}
            Add Site
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
